<script setup>
import { computed, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { conversationUrl, frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import {
  cortexKnowledgeBasesAPI,
  cortexProcessingRunsAPI,
} from 'dashboard/api/cortex';
import inboxesAPI from 'dashboard/api/inboxes';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import Label from 'dashboard/components-next/label/Label.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import {
  cortexProcessingErrorMessage,
  cortexProcessingPreviewRequiresRefresh,
} from 'dashboard/helper/cortexProcessingError';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import SectionLayout from '../../settings/account/components/SectionLayout.vue';
import CortexProcessingMediaDetails from './CortexProcessingMediaDetails.vue';
import CortexProcessingBudgetDialog from './CortexProcessingBudgetDialog.vue';
import CortexBudgetReconciliationDialog from './CortexBudgetReconciliationDialog.vue';

const props = defineProps({
  activeSection: {
    type: String,
    default: 'new',
    validator: value => ['new', 'history'].includes(value),
  },
});

const emit = defineEmits(['viewResults']);
const { t } = useI18n();
const route = useRoute();
const loading = ref(false);
const previewing = ref(false);
const starting = ref(false);
const actingRunId = ref(null);
const preview = ref(null);
const maximumCost = ref('');
const startIdempotencyKey = ref('');
const runs = ref([]);
const totalRuns = ref(0);
const page = ref(1);
const perPage = ref(10);
const knowledgeBases = ref([]);
const inboxes = ref([]);
const confirmationDialog = ref(null);
const detailsDialog = ref(null);
const budgetDialog = ref(null);
const mediaDetails = ref(null);
const reconciliationDialog = ref(null);
const selectedRun = ref(null);
const loadingRunDetails = ref(false);
const targetPage = ref(1);
const targetPerPage = ref(10);
const POLLING_INTERVAL = 5000;
const historyFilters = reactive({
  knowledge_base_id: '',
  status: '',
  trigger: '',
  since: '',
  until: '',
});
let pollingTimer;
let detailsPollingTimer;

const today = new Date();
const sevenDaysAgo = new Date(today);
sevenDaysAgo.setUTCDate(today.getUTCDate() - 6);
const dateValue = value => value.toISOString().slice(0, 10);
const form = reactive({
  knowledge_base_id: null,
  inbox_ids: [],
  statuses: ['resolved'],
  since: dateValue(sevenDaysAgo),
  until: dateValue(today),
  limit: 100,
});

const knowledgeBaseOptions = computed(() =>
  knowledgeBases.value.map(item => ({ value: item.id, label: item.name }))
);
const historyKnowledgeBaseOptions = computed(() => [
  {
    value: '',
    label: t('CORTEX.PROCESSING.V6.FILTERS.ALL_KNOWLEDGE_BASES'),
  },
  ...knowledgeBaseOptions.value,
]);
const inboxOptions = computed(() =>
  inboxes.value.map(item => ({ value: item.id, label: item.name }))
);
const statusOptions = computed(() => [
  { value: 'open', label: t('CORTEX.PROCESSING.V6.STATUSES.OPEN') },
  { value: 'pending', label: t('CORTEX.PROCESSING.V6.STATUSES.PENDING') },
  { value: 'snoozed', label: t('CORTEX.PROCESSING.V6.STATUSES.SNOOZED') },
  { value: 'resolved', label: t('CORTEX.PROCESSING.V6.STATUSES.RESOLVED') },
]);
const limitOptions = computed(() => [
  ...[50, 100, 500, 1000, 5000, 10000, 20000].map(value => ({
    value,
    label: new Intl.NumberFormat().format(value),
  })),
  { value: 'all', label: t('CORTEX.PROCESSING.V6.LIMIT_UNLIMITED') },
]);
const runStatusLabels = computed(() => ({
  pending: t('CORTEX.PROCESSING.V6.RUN_STATUSES.PENDING'),
  running: t('CORTEX.PROCESSING.V6.RUN_STATUSES.RUNNING'),
  paused: t('CORTEX.PROCESSING.V6.RUN_STATUSES.PAUSED'),
  completed: t('CORTEX.PROCESSING.V6.RUN_STATUSES.COMPLETED'),
  partial: t('CORTEX.PROCESSING.V6.RUN_STATUSES.PARTIAL'),
  failed: t('CORTEX.PROCESSING.V6.RUN_STATUSES.FAILED'),
  cancelled: t('CORTEX.PROCESSING.V6.RUN_STATUSES.CANCELLED'),
}));
const triggerLabels = computed(() => ({
  manual_bulk: t('CORTEX.PROCESSING.V6.TRIGGERS.MANUAL_BULK'),
  manual_single: t('CORTEX.PROCESSING.V6.TRIGGERS.MANUAL_SINGLE'),
}));
const targetStatusLabels = computed(() => ({
  pending: t('CORTEX.PROCESSING.V6.TARGET_STATUSES.PENDING'),
  processing: t('CORTEX.PROCESSING.V6.TARGET_STATUSES.PROCESSING'),
  completed: t('CORTEX.PROCESSING.V6.TARGET_STATUSES.COMPLETED'),
  failed: t('CORTEX.PROCESSING.V6.TARGET_STATUSES.FAILED'),
  cancelled: t('CORTEX.PROCESSING.V6.TARGET_STATUSES.CANCELLED'),
  skipped: t('CORTEX.PROCESSING.V6.TARGET_STATUSES.SKIPPED'),
}));
const runStatusOptions = computed(() => [
  { value: '', label: t('CORTEX.PROCESSING.V6.FILTERS.ALL_STATUSES') },
  ...Object.entries(runStatusLabels.value).map(([value, label]) => ({
    value,
    label,
  })),
]);
const triggerOptions = computed(() => [
  { value: '', label: t('CORTEX.PROCESSING.V6.FILTERS.ALL_TRIGGERS') },
  {
    value: 'manual_bulk',
    label: t('CORTEX.PROCESSING.V6.TRIGGERS.MANUAL_BULK'),
  },
  {
    value: 'manual_single',
    label: t('CORTEX.PROCESSING.V6.TRIGGERS.MANUAL_SINGLE'),
  },
]);
const canPreview = computed(
  () =>
    form.knowledge_base_id &&
    form.inbox_ids.length &&
    form.statuses.length &&
    form.since &&
    form.until &&
    form.since <= form.until &&
    (form.limit === 'all' || form.limit > 0)
);
const canStart = computed(() => {
  const amount = String(maximumCost.value).trim();
  return (
    preview.value &&
    preview.value.budget_quote?.ready &&
    /^\d+(?:\.\d{1,6})?$/.test(amount) &&
    Number(amount) < 1_000_000 &&
    Number(amount) >= Number(preview.value.budget_quote.minimum_cost)
  );
});
const headers = computed(() => [
  t('CORTEX.PROCESSING.V6.TABLE.ID'),
  t('CORTEX.PROCESSING.V6.TABLE.TRIGGER'),
  t('CORTEX.PROCESSING.V6.TABLE.STATUS'),
  t('CORTEX.PROCESSING.V6.TABLE.PROGRESS'),
  t('CORTEX.PROCESSING.V6.TABLE.ACTIONS'),
]);
const targetHeaders = computed(() => [
  t('CORTEX.PROCESSING.V6.DETAILS.CONVERSATION'),
  t('CORTEX.PROCESSING.V6.DETAILS.INBOX'),
  t('CORTEX.PROCESSING.V6.DETAILS.STATUS'),
  t('CORTEX.PROCESSING.V6.DETAILS.MESSAGES'),
  t('CORTEX.PROCESSING.V6.DETAILS.ERROR'),
  t('CORTEX.PROCESSING.V6.TABLE.ACTIONS'),
]);
const conversationLink = target =>
  frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: target.conversation_display_id,
    })
  );
const viewResults = target => {
  if (!target.assessment_id) return;
  detailsDialog.value?.close();
  emit('viewResults', {
    assessmentId: target.assessment_id,
    conversationDisplayId: target.conversation_display_id,
  });
};
const filters = () => ({
  knowledge_base_id: form.knowledge_base_id,
  inbox_ids: form.inbox_ids,
  statuses: form.statuses,
  since: `${form.since}T00:00:00.000Z`,
  until: `${form.until}T23:59:59.999Z`,
  limit: form.limit,
});
const mutationKey = (run, action) =>
  `processing-run-${run.id}-${action}-${Date.now()}`;
const runStatusLabel = status => runStatusLabels.value[status] || status;
const runTriggerLabel = trigger => triggerLabels.value[trigger] || trigger;
const targetStatusLabel = status => targetStatusLabels.value[status] || status;
const statusColor = status =>
  ({
    pending: 'amber',
    running: 'brand',
    paused: 'slate',
    completed: 'teal',
    partial: 'amber',
    failed: 'ruby',
    cancelled: 'slate',
    processing: 'brand',
    skipped: 'slate',
  })[status] || 'slate';
const formatNumber = value =>
  new Intl.NumberFormat().format(Number(value || 0));
const formatCurrency = value =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 6,
  }).format(Number(value || 0));
const formatDateTime = value =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value))
    : t('CORTEX.PROCESSING.V6.NOT_AVAILABLE');
const formatDate = value =>
  value
    ? new Intl.DateTimeFormat(undefined, { dateStyle: 'short' }).format(
        new Date(value)
      )
    : t('CORTEX.PROCESSING.V6.NOT_AVAILABLE');
const runPeriod = run => {
  const since = run.filters?.since;
  const until = run.filters?.until;
  if (!since || !until) return t('CORTEX.PROCESSING.V6.NOT_AVAILABLE');

  return t('CORTEX.PROCESSING.V6.DETAILS.PERIOD_VALUE', {
    since: formatDate(since),
    until: formatDate(until),
  });
};
const runDuration = run => {
  if (!run.started_at) return t('CORTEX.PROCESSING.V6.NOT_AVAILABLE');

  const end = run.finished_at ? new Date(run.finished_at) : new Date();
  const seconds = Math.max(
    Math.round((end - new Date(run.started_at)) / 1000),
    0
  );
  return t('CORTEX.PROCESSING.V6.DETAILS.DURATION_VALUE', {
    minutes: Math.floor(seconds / 60),
    seconds: seconds % 60,
  });
};
const progressFor = run =>
  run.processing_phase === 'media' && run.media_progress
    ? run.media_progress
    : run;
const phaseLabels = computed(() => ({
  preparing: t('CORTEX.PROCESSING.V6.PHASES.PREPARING'),
  media: t('CORTEX.PROCESSING.V6.PHASES.MEDIA'),
  text: t('CORTEX.PROCESSING.V6.PHASES.TEXT'),
}));
const progressLabel = run => {
  const progress = progressFor(run);
  const values = {
    processed: formatNumber(progress.processed_count),
    selected: formatNumber(progress.selected_count),
  };
  return run.processing_phase === 'media'
    ? t('CORTEX.PROCESSING.V6.MEDIA_PROGRESS_VALUE', values)
    : t('CORTEX.PROCESSING.V6.PROGRESS_VALUE', values);
};
const progressPercentage = run =>
  progressFor(run).selected_count
    ? Math.min(
        Math.round(
          (progressFor(run).processed_count / progressFor(run).selected_count) *
            100
        ),
        100
      )
    : 0;
const isRunActive = run => ['pending', 'running'].includes(run?.status);
const isSelectedRunActive = computed(() => isRunActive(selectedRun.value));

const loadOptions = async () => {
  try {
    const [basesResponse, inboxesResponse] = await Promise.all([
      cortexKnowledgeBasesAPI.get(),
      inboxesAPI.get(),
    ]);
    knowledgeBases.value =
      basesResponse.data?.payload || basesResponse.data || [];
    inboxes.value = inboxesResponse.data?.payload || inboxesResponse.data || [];
    form.knowledge_base_id ||= knowledgeBases.value[0]?.id;
  } catch (error) {
    useAlert(t('CORTEX.PROCESSING.V6.API.LOAD_ERROR'));
  }
};

async function loadRuns({ silent = false } = {}) {
  if (!silent) loading.value = true;
  try {
    const { data, headers: responseHeaders } =
      await cortexProcessingRunsAPI.list({
        page: page.value,
        per_page: perPage.value,
        knowledge_base_id: historyFilters.knowledge_base_id || undefined,
        status: historyFilters.status || undefined,
        trigger: historyFilters.trigger || undefined,
        created_since: historyFilters.since
          ? `${historyFilters.since}T00:00:00.000Z`
          : undefined,
        created_until: historyFilters.until
          ? `${historyFilters.until}T23:59:59.999Z`
          : undefined,
      });
    runs.value = data;
    totalRuns.value = Number(responseHeaders['x-total-count'] || data.length);
    clearTimeout(pollingTimer);
    if (runs.value.some(isRunActive)) {
      pollingTimer = setTimeout(
        () => loadRuns({ silent: true }),
        POLLING_INTERVAL
      );
    }
  } catch (error) {
    useAlert(t('CORTEX.PROCESSING.V6.API.LOAD_ERROR'));
  } finally {
    if (!silent) loading.value = false;
  }
}

const preparePreview = async () => {
  if (!canPreview.value) return;
  previewing.value = true;
  try {
    const { data } = await cortexProcessingRunsAPI.preview({
      filters: filters(),
    });
    preview.value = data;
    maximumCost.value = '';
    startIdempotencyKey.value = `bulk-${data.selection_fingerprint}-${Date.now()}`;
    confirmationDialog.value?.open();
  } catch (error) {
    useAlert(
      cortexProcessingErrorMessage(
        error,
        t,
        t('CORTEX.PROCESSING.V6.API.PREVIEW_ERROR')
      )
    );
  } finally {
    previewing.value = false;
  }
};

const startProcessing = async () => {
  if (!canStart.value || starting.value) return;
  starting.value = true;
  try {
    await cortexProcessingRunsAPI.start(
      {
        preview_token: preview.value.preview_token,
        authorization_reason: 'historical_quality_review',
        maximum_cost: String(maximumCost.value).trim(),
      },
      startIdempotencyKey.value
    );
    confirmationDialog.value?.close();
    preview.value = null;
    page.value = 1;
    await loadRuns();
    useAlert(t('CORTEX.PROCESSING.V6.API.STARTED'));
  } catch (error) {
    if (cortexProcessingPreviewRequiresRefresh(error)) {
      confirmationDialog.value?.close();
      preview.value = null;
    }
    useAlert(
      cortexProcessingErrorMessage(
        error,
        t,
        t('CORTEX.PROCESSING.V6.API.START_ERROR')
      )
    );
  } finally {
    starting.value = false;
  }
};

async function loadRunDetails(runId, { silent = false } = {}) {
  clearTimeout(detailsPollingTimer);
  if (!silent) loadingRunDetails.value = true;
  try {
    const { data } = await cortexProcessingRunsAPI.show(runId, {
      target_page: targetPage.value,
      target_per_page: targetPerPage.value,
    });
    if (selectedRun.value?.id !== runId) return;
    selectedRun.value = data;
  } catch (error) {
    if (!silent) useAlert(t('CORTEX.PROCESSING.V6.API.DETAIL_ERROR'));
  } finally {
    if (!silent) loadingRunDetails.value = false;
    if (isSelectedRunActive.value) {
      detailsPollingTimer = setTimeout(() => {
        const selectedRunId = selectedRun.value?.id;
        if (selectedRunId) loadRunDetails(selectedRunId, { silent: true });
      }, POLLING_INTERVAL);
    }
  }
}

const runAction = async (run, action) => {
  actingRunId.value = run.id;
  try {
    await cortexProcessingRunsAPI[action](
      run.id,
      { lock_version: run.lock_version },
      mutationKey(run, action)
    );
    await loadRuns();
    if (selectedRun.value?.id === run.id) await loadRunDetails(run.id);
  } catch (error) {
    useAlert(t('CORTEX.PROCESSING.V6.API.ACTION_ERROR'));
  } finally {
    actingRunId.value = null;
  }
};

const openRunDetails = async run => {
  clearTimeout(detailsPollingTimer);
  targetPage.value = 1;
  targetPerPage.value = 10;
  selectedRun.value = run;
  detailsDialog.value?.open();
  await loadRunDetails(run.id);
};

const closeRunDetails = () => {
  clearTimeout(detailsPollingTimer);
  selectedRun.value = null;
};

const refreshBudget = async runId => {
  await loadRuns();
  if (selectedRun.value?.id === runId) await loadRunDetails(runId);
};

const updateTargetPage = async nextPage => {
  targetPage.value = nextPage;
  await loadRunDetails(selectedRun.value.id);
};

const updateTargetPageSize = async nextPageSize => {
  targetPage.value = 1;
  targetPerPage.value = nextPageSize;
  await loadRunDetails(selectedRun.value.id);
};

const applyHistoryFilters = async () => {
  page.value = 1;
  await loadRuns();
};

watch([page, perPage], () => loadRuns());
onMounted(async () => {
  await Promise.all([loadOptions(), loadRuns()]);
});
onUnmounted(() => {
  clearTimeout(pollingTimer);
  clearTimeout(detailsPollingTimer);
});
</script>

<template>
  <div class="flex min-w-0 flex-col gap-6">
    <SectionLayout
      v-show="props.activeSection === 'new'"
      :title="$t('CORTEX.PROCESSING.WORKSPACE.NEW_TITLE')"
      :description="$t('CORTEX.PROCESSING.WORKSPACE.NEW_DESCRIPTION')"
    >
      <form
        class="grid min-w-0 gap-4 md:grid-cols-2"
        data-testid="processing-form"
        @submit.prevent="preparePreview"
      >
        <WithLabel
          name="cortex-v6-knowledge-base"
          :label="$t('CORTEX.PROCESSING.V6.KNOWLEDGE_BASE')"
        >
          <AdaptiveSelect
            v-model="form.knowledge_base_id"
            :options="knowledgeBaseOptions"
            :placeholder="$t('CORTEX.PROCESSING.V6.SELECT_KNOWLEDGE_BASE')"
            :aria-label="$t('CORTEX.PROCESSING.V6.KNOWLEDGE_BASE')"
          />
        </WithLabel>
        <WithLabel
          name="cortex-v6-inboxes"
          :label="$t('CORTEX.PROCESSING.V6.INBOXES')"
        >
          <AdaptiveSelect
            v-model="form.inbox_ids"
            mode="multiple"
            :options="inboxOptions"
            :placeholder="$t('CORTEX.PROCESSING.V6.SELECT_INBOXES')"
          />
        </WithLabel>
        <WithLabel
          name="cortex-v6-statuses"
          :label="$t('CORTEX.PROCESSING.V6.STATUSES_LABEL')"
        >
          <AdaptiveSelect
            v-model="form.statuses"
            mode="multiple"
            :options="statusOptions"
            :placeholder="$t('CORTEX.PROCESSING.V6.SELECT_STATUSES')"
          />
        </WithLabel>
        <WithLabel
          name="cortex-v6-limit"
          :label="$t('CORTEX.PROCESSING.V6.LIMIT')"
          :help-message="$t('CORTEX.PROCESSING.V6.LIMIT_HELP')"
        >
          <AdaptiveSelect
            v-model="form.limit"
            :options="limitOptions"
            :aria-label="$t('CORTEX.PROCESSING.V6.LIMIT')"
          />
        </WithLabel>
        <NextInput
          v-model="form.since"
          type="date"
          :label="$t('CORTEX.PROCESSING.V6.SINCE')"
        />
        <NextInput
          v-model="form.until"
          type="date"
          :label="$t('CORTEX.PROCESSING.V6.UNTIL')"
        />
        <div class="flex justify-end md:col-span-2">
          <NextButton
            type="submit"
            blue
            icon="i-lucide-search-check"
            :disabled="!canPreview"
            :is-loading="previewing"
            :label="$t('CORTEX.PROCESSING.V6.PREVIEW')"
          />
        </div>
      </form>
    </SectionLayout>

    <section
      v-show="props.activeSection === 'history'"
      class="grid min-w-0 gap-4 rounded-lg border border-n-weak bg-n-surface-1 px-4 py-4"
      data-testid="processing-history"
    >
      <div class="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ $t('CORTEX.PROCESSING.V6.HISTORY.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('CORTEX.PROCESSING.V6.HISTORY.DESCRIPTION') }}
          </p>
        </div>
        <NextButton
          type="button"
          sm
          slate
          ghost
          icon="i-lucide-refresh-cw"
          :label="$t('CORTEX.PROCESSING.V6.REFRESH')"
          :is-loading="loading"
          @click="loadRuns()"
        />
      </div>
      <form
        class="grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
        @submit.prevent="applyHistoryFilters"
      >
        <WithLabel
          name="cortex-processing-history-knowledge-base"
          :label="$t('CORTEX.PROCESSING.V6.FILTERS.KNOWLEDGE_BASE')"
        >
          <AdaptiveSelect
            v-model="historyFilters.knowledge_base_id"
            :options="historyKnowledgeBaseOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-processing-history-status"
          :label="$t('CORTEX.PROCESSING.V6.FILTERS.STATUS')"
        >
          <AdaptiveSelect
            v-model="historyFilters.status"
            :options="runStatusOptions"
          />
        </WithLabel>
        <WithLabel
          name="cortex-processing-history-trigger"
          :label="$t('CORTEX.PROCESSING.V6.FILTERS.TRIGGER')"
        >
          <AdaptiveSelect
            v-model="historyFilters.trigger"
            :options="triggerOptions"
          />
        </WithLabel>
        <NextInput
          v-model="historyFilters.since"
          type="date"
          :label="$t('CORTEX.PROCESSING.V6.FILTERS.SINCE')"
        />
        <NextInput
          v-model="historyFilters.until"
          type="date"
          :label="$t('CORTEX.PROCESSING.V6.FILTERS.UNTIL')"
        />
        <div class="flex justify-end sm:col-span-2 lg:col-span-5">
          <NextButton
            type="submit"
            sm
            icon="i-lucide-list-filter"
            :label="$t('CORTEX.PROCESSING.V6.FILTERS.APPLY')"
            :is-loading="loading"
          />
        </div>
      </form>
      <BaseTable
        :headers="headers"
        :items="runs"
        :loading="loading"
        :loading-message="$t('CORTEX.PROCESSING.V6.LOADING')"
        :no-data-message="$t('CORTEX.PROCESSING.V6.EMPTY')"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="run in items" :key="run.id" :item="run">
            <BaseTableCell>
              <div class="grid gap-1">
                <span class="font-medium text-n-slate-12">
                  {{ $t('CORTEX.PROCESSING.V6.DETAILS.TITLE', { id: run.id }) }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ run.knowledge_base_name }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell>
              <div class="grid gap-1">
                <span class="text-n-slate-12">
                  {{ runTriggerLabel(run.trigger) }}
                </span>
                <span class="text-xs text-n-slate-11">
                  {{ formatDateTime(run.created_at) }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell>
              <Label
                compact
                :label="runStatusLabel(run.status)"
                :color="statusColor(run.status)"
              >
                <template #icon>
                  <span
                    v-if="run.status === 'running'"
                    class="i-lucide-loader-2 size-3.5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                </template>
              </Label>
            </BaseTableCell>
            <BaseTableCell>
              <div class="grid min-w-40 gap-1.5">
                <span
                  v-if="run.processing_phase"
                  class="text-xs leading-4 text-n-slate-11"
                >
                  {{ phaseLabels[run.processing_phase] }}
                </span>
                <span class="text-n-slate-12">
                  {{ progressLabel(run) }}
                </span>
                <span
                  v-if="run.blocking_message"
                  class="text-xs leading-4 text-n-ruby-11"
                >
                  {{ run.blocking_message }}
                </span>
                <progress
                  class="h-1.5 w-full accent-n-brand"
                  max="100"
                  :value="progressPercentage(run)"
                  :aria-label="$t('CORTEX.PROCESSING.V6.TABLE.PROGRESS')"
                />
                <span class="text-xs leading-4 text-n-slate-11">
                  {{
                    $t('CORTEX.PROCESSING.V6.RESULTS_VALUE', {
                      succeeded: formatNumber(progressFor(run).succeeded_count),
                      failed: formatNumber(progressFor(run).failed_count),
                      candidates: formatNumber(run.candidate_count),
                    })
                  }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell
              align="end"
              class="sticky right-0 z-10 bg-n-surface-1"
            >
              <div class="flex justify-end gap-1 whitespace-nowrap">
                <NextButton
                  type="button"
                  sm
                  slate
                  ghost
                  icon="i-lucide-eye"
                  class="!text-[color:inherit]"
                  :aria-label="$t('CORTEX.PROCESSING.V6.VIEW_DETAILS')"
                  :title="$t('CORTEX.PROCESSING.V6.VIEW_DETAILS')"
                  @click="openRunDetails(run)"
                />
                <NextButton
                  v-if="['partial', 'failed', 'paused'].includes(run.status)"
                  type="button"
                  sm
                  slate
                  ghost
                  icon="i-lucide-play"
                  class="!text-[color:inherit]"
                  :is-loading="actingRunId === run.id"
                  :aria-label="$t('CORTEX.PROCESSING.V6.RESUME')"
                  :title="$t('CORTEX.PROCESSING.V6.RESUME')"
                  @click="runAction(run, 'resume')"
                />
                <NextButton
                  v-if="['pending', 'running', 'paused'].includes(run.status)"
                  type="button"
                  sm
                  ruby
                  ghost
                  icon="i-lucide-circle-stop"
                  class="!text-[color:inherit]"
                  :is-loading="actingRunId === run.id"
                  :aria-label="$t('CORTEX.PROCESSING.V6.CANCEL')"
                  :title="$t('CORTEX.PROCESSING.V6.CANCEL')"
                  @click="runAction(run, 'cancel')"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
      <PaginationFooter
        v-model:current-page="page"
        v-model:items-per-page="perPage"
        :total-items="totalRuns"
        show-page-size-selector
      />
    </section>

    <Dialog
      ref="detailsDialog"
      width="6xl"
      position="top"
      overflow-y-auto
      :title="$t('CORTEX.PROCESSING.V6.DETAILS.TITLE', { id: selectedRun?.id })"
      :description="$t('CORTEX.PROCESSING.V6.DETAILS.DESCRIPTION')"
      :cancel-button-label="$t('CORTEX.PROCESSING.V6.DETAILS.CLOSE')"
      cancel-button-icon="i-lucide-x"
      :show-confirm-button="false"
      @close="closeRunDetails"
    >
      <div v-if="selectedRun" class="grid gap-5">
        <div class="flex flex-wrap items-center justify-end gap-2">
          <div
            v-if="isSelectedRunActive"
            class="inline-flex items-center gap-1.5 text-xs text-n-slate-11"
            data-testid="processing-live-indicator"
            role="status"
          >
            <span
              class="i-lucide-refresh-cw size-3.5 animate-spin text-n-brand motion-reduce:animate-none"
              aria-hidden="true"
            />
            <span>
              {{ $t('CORTEX.PROCESSING.V6.DETAILS.LIVE_UPDATES') }}
            </span>
          </div>
          <NextButton
            v-if="selectedRun.media_progress"
            type="button"
            sm
            slate
            ghost
            icon="i-lucide-scan-search"
            :label="$t('CORTEX.PROCESSING.V6.MEDIA_RESULTS')"
            @click="mediaDetails?.open(selectedRun)"
          />
          <NextButton
            type="button"
            sm
            slate
            ghost
            icon="i-lucide-refresh-cw"
            :label="$t('CORTEX.PROCESSING.V6.REFRESH')"
            :is-loading="loadingRunDetails"
            @click="loadRunDetails(selectedRun.id)"
          />
          <NextButton
            v-if="['partial', 'failed', 'paused'].includes(selectedRun.status)"
            type="button"
            sm
            slate
            ghost
            icon="i-lucide-play"
            :is-loading="actingRunId === selectedRun.id"
            :aria-label="$t('CORTEX.PROCESSING.V6.RESUME')"
            :title="$t('CORTEX.PROCESSING.V6.RESUME')"
            @click="runAction(selectedRun, 'resume')"
          />
          <NextButton
            v-if="
              selectedRun.can_manage_budget &&
              selectedRun.budget?.version &&
              selectedRun.status !== 'cancelled'
            "
            type="button"
            sm
            slate
            ghost
            icon="i-lucide-wallet"
            data-testid="processing-budget-button"
            :label="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.ACTION')"
            @click="budgetDialog?.open(selectedRun)"
          />
          <NextButton
            v-if="selectedRun.can_manage_budget && selectedRun.budget?.version"
            type="button"
            sm
            slate
            ghost
            icon="i-lucide-receipt-text"
            data-testid="processing-reconciliation-button"
            :label="$t('CORTEX_BUDGET_RECONCILIATION.ACTION')"
            @click="reconciliationDialog?.open(selectedRun)"
          />
          <NextButton
            v-if="['pending', 'running', 'paused'].includes(selectedRun.status)"
            type="button"
            sm
            ruby
            ghost
            icon="i-lucide-circle-stop"
            :is-loading="actingRunId === selectedRun.id"
            :aria-label="$t('CORTEX.PROCESSING.V6.CANCEL')"
            :title="$t('CORTEX.PROCESSING.V6.CANCEL')"
            @click="runAction(selectedRun, 'cancel')"
          />
        </div>
        <div
          v-if="loadingRunDetails"
          class="flex items-center justify-center gap-2 py-8 text-sm text-n-slate-11"
          role="status"
        >
          <span class="i-lucide-loader-2 size-5 animate-spin" />
          {{ $t('CORTEX.PROCESSING.V6.DETAILS.LOADING') }}
        </div>
        <template v-else>
          <dl class="grid gap-3 sm:grid-cols-2 lg:grid-cols-6">
            <div
              class="flex min-h-20 flex-col justify-between rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.SELECTED') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatNumber(selectedRun.selected_count) }}
              </dd>
            </div>
            <div
              class="flex min-h-20 flex-col justify-between rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.PROCESSED') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatNumber(selectedRun.processed_count) }}
              </dd>
            </div>
            <div
              class="flex min-h-20 flex-col justify-between rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.SUCCEEDED') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatNumber(selectedRun.succeeded_count) }}
              </dd>
            </div>
            <div
              class="flex min-h-20 flex-col justify-between rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.FAILED') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatNumber(selectedRun.failed_count) }}
              </dd>
            </div>
            <div
              class="flex min-h-20 flex-col justify-between rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.CANDIDATES') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatNumber(selectedRun.candidate_count) }}
              </dd>
            </div>
            <div
              class="flex min-h-20 flex-col justify-between rounded-lg bg-n-alpha-2 p-3"
            >
              <dt class="text-xs text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.ACTUAL_COST') }}
              </dt>
              <dd class="text-lg font-medium text-n-slate-12">
                {{ formatCurrency(selectedRun.actual_cost) }}
              </dd>
            </div>
          </dl>

          <dl class="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.MAXIMUM_COST') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ formatCurrency(selectedRun.budget_cost) }}
              </dd>
            </div>
            <template v-if="selectedRun.budget?.version">
              <div>
                <dt class="text-n-slate-11">
                  {{ $t('CORTEX.PROCESSING.V6.RESERVED_COST') }}
                </dt>
                <dd class="mt-1 font-medium text-n-slate-12">
                  {{ formatCurrency(selectedRun.budget.reserved_cost) }}
                </dd>
              </div>
              <div>
                <dt class="text-n-slate-11">
                  {{ $t('CORTEX.PROCESSING.V6.AVAILABLE_COST') }}
                </dt>
                <dd class="mt-1 font-medium text-n-slate-12">
                  {{ formatCurrency(selectedRun.budget.available_cost) }}
                </dd>
              </div>
              <div v-if="selectedRun.budget.uncertain_attempts">
                <dt class="text-n-slate-11">
                  {{ $t('CORTEX.PROCESSING.V6.UNCERTAIN_ATTEMPTS') }}
                </dt>
                <dd class="mt-1 font-medium text-n-slate-12">
                  {{ formatNumber(selectedRun.budget.uncertain_attempts) }}
                </dd>
              </div>
            </template>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.TABLE.STATUS') }}
              </dt>
              <dd class="mt-1">
                <Label
                  compact
                  :label="runStatusLabel(selectedRun.status)"
                  :color="statusColor(selectedRun.status)"
                >
                  <template #icon>
                    <span
                      v-if="selectedRun.status === 'running'"
                      class="i-lucide-loader-2 size-3.5 animate-spin motion-reduce:animate-none"
                      aria-hidden="true"
                    />
                  </template>
                </Label>
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.TABLE.TRIGGER') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ runTriggerLabel(selectedRun.trigger) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.CUTOFF') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ formatDateTime(selectedRun.source_cutoff_at) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.PERIOD') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ runPeriod(selectedRun) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.DURATION') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ runDuration(selectedRun) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.FINISHED_AT') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ formatDateTime(selectedRun.finished_at) }}
              </dd>
            </div>
            <div>
              <dt class="text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.AUTHORIZATION_ENDS_AT') }}
              </dt>
              <dd class="mt-1 font-medium text-n-slate-12">
                {{ formatDateTime(selectedRun.authorization_ends_at) }}
              </dd>
            </div>
          </dl>

          <section class="grid gap-3 border-t border-n-weak pt-4">
            <div>
              <h4 class="text-sm font-medium text-n-slate-12">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.TARGETS_TITLE') }}
              </h4>
              <p class="mt-1 text-xs text-n-slate-11">
                {{ $t('CORTEX.PROCESSING.V6.DETAILS.TARGETS_DESCRIPTION') }}
              </p>
            </div>
            <BaseTable
              :headers="targetHeaders"
              :items="selectedRun.targets || []"
              :no-data-message="$t('CORTEX.PROCESSING.V6.DETAILS.EMPTY')"
            >
              <template #row="{ items }">
                <BaseTableRow
                  v-for="target in items"
                  :key="target.id"
                  :item="target"
                >
                  <BaseTableCell>
                    {{
                      $t('CORTEX.PROCESSING.V6.DETAILS.CONVERSATION_VALUE', {
                        id: target.conversation_display_id,
                      })
                    }}
                  </BaseTableCell>
                  <BaseTableCell>{{ target.inbox_name }}</BaseTableCell>
                  <BaseTableCell>
                    <Label
                      compact
                      :label="targetStatusLabel(target.status)"
                      :color="statusColor(target.status)"
                    >
                      <template #icon>
                        <span
                          v-if="target.status === 'processing'"
                          class="i-lucide-loader-2 size-3.5 animate-spin motion-reduce:animate-none"
                          aria-hidden="true"
                        />
                      </template>
                    </Label>
                  </BaseTableCell>
                  <BaseTableCell>
                    {{ formatNumber(target.message_count) }}
                  </BaseTableCell>
                  <BaseTableCell>
                    <span
                      class="block max-w-80 truncate"
                      :title="target.error_message || undefined"
                    >
                      {{
                        target.error_message ||
                        $t('CORTEX.PROCESSING.V6.DETAILS.NO_ERROR')
                      }}
                    </span>
                  </BaseTableCell>
                  <BaseTableCell align="end" class="align-middle">
                    <div class="inline-flex items-center justify-end gap-1">
                      <a
                        :href="conversationLink(target)"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="inline-flex size-8 items-center justify-center rounded-lg !text-[color:inherit] outline-1 outline-transparent hover:bg-n-alpha-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-n-brand"
                        :title="
                          $t('CORTEX.PROCESSING.V6.DETAILS.OPEN_CONVERSATION')
                        "
                        :aria-label="
                          $t('CORTEX.PROCESSING.V6.DETAILS.OPEN_CONVERSATION')
                        "
                      >
                        <span
                          class="i-lucide-message-square-text size-4"
                          aria-hidden="true"
                        />
                      </a>
                      <NextButton
                        type="button"
                        sm
                        slate
                        ghost
                        icon="i-lucide-list-checks"
                        class="!text-[color:inherit]"
                        :disabled="!target.assessment_id"
                        :title="$t('CORTEX.PROCESSING.V6.DETAILS.VIEW_RESULTS')"
                        :aria-label="
                          $t('CORTEX.PROCESSING.V6.DETAILS.VIEW_RESULTS')
                        "
                        @click="viewResults(target)"
                      />
                    </div>
                  </BaseTableCell>
                </BaseTableRow>
              </template>
            </BaseTable>
            <PaginationFooter
              :current-page="selectedRun.target_pagination?.page || 1"
              :items-per-page="selectedRun.target_pagination?.per_page || 10"
              :total-items="selectedRun.target_pagination?.total || 0"
              show-page-size-selector
              @update:current-page="updateTargetPage"
              @update:items-per-page="updateTargetPageSize"
            />
          </section>
        </template>
      </div>
    </Dialog>

    <CortexProcessingMediaDetails ref="mediaDetails" />
    <CortexProcessingBudgetDialog ref="budgetDialog" @saved="refreshBudget" />
    <CortexBudgetReconciliationDialog
      ref="reconciliationDialog"
      @saved="refreshBudget"
    />
    <Dialog
      ref="confirmationDialog"
      width="md"
      :title="$t('CORTEX.PROCESSING.V6.CONFIRM_TITLE')"
      :confirm-button-label="$t('CORTEX.PROCESSING.V6.START')"
      confirm-button-icon="i-lucide-play"
      cancel-button-icon="i-lucide-x"
      :is-loading="starting"
      :disable-confirm-button="!canStart"
      @confirm="startProcessing"
    >
      <div v-if="preview" class="grid gap-4 text-sm text-n-slate-11">
        <dl
          class="grid grid-cols-2 gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-3"
        >
          <div>
            <dt>{{ $t('CORTEX.PROCESSING.V6.CONVERSATIONS') }}</dt>
            <dd class="font-medium text-n-slate-12">
              {{ formatNumber(preview.conversation_count) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('CORTEX.PROCESSING.V6.MESSAGES') }}</dt>
            <dd class="font-medium text-n-slate-12">
              {{ formatNumber(preview.message_count) }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('CORTEX.PROCESSING.V6.COST') }}</dt>
            <dd class="font-medium text-n-slate-12">
              {{
                preview.budget_quote?.ready
                  ? formatCurrency(preview.budget_quote.minimum_cost)
                  : $t('CORTEX.PROCESSING.V6.BUDGET_UNAVAILABLE')
              }}
            </dd>
          </div>
          <div>
            <dt>{{ $t('CORTEX.PROCESSING.V6.CUTOFF') }}</dt>
            <dd class="font-medium text-n-slate-12">
              {{ formatDateTime(preview.source_cutoff_at) }}
            </dd>
          </div>
        </dl>
        <p v-if="!preview.budget_quote?.ready" role="alert">
          {{ $t('CORTEX.PROCESSING.V6.BUDGET_UNAVAILABLE_HELP') }}
        </p>
        <NextInput
          v-model="maximumCost"
          type="number"
          min="0"
          max="999999.999999"
          step="0.000001"
          :label="$t('CORTEX.PROCESSING.V6.MAXIMUM_COST')"
          :message="$t('CORTEX.PROCESSING.V6.MAXIMUM_COST_HELP')"
        />
        <p>{{ $t('CORTEX.PROCESSING.V6.MANUAL_AUTH_NOTICE') }}</p>
      </div>
    </Dialog>
  </div>
</template>
